import React from "react";
import Div from "../lib/Div";
import P from "../lib/P";
import Btn from "./Btn";
import {WagerMode} from "../../generated/bay_session_shared_pb";
import {useWagerCurrency} from "../hooks/useWagerCurrency";

/**
 * WagerCurrencySelector — the SMG-3446 Real Money vs Points CATEGORY picker.
 *
 * Single-availability rule (the product AC): renders ONLY when the station
 * offers ≥2 wager categories for this player. When exactly one is available
 * the sole category is auto-selected upstream (useWagerCurrency) and this
 * component renders nothing — no toggle, no way to change.
 *
 * Selecting a category drives SetWagerMode (no live wager) or
 * SwitchWagerCurrency (live wager) via the hook; the chosen category then
 * flows into AcceptBet's currency string.
 */
const CATEGORY_LABEL: Record<WagerMode.MONEY | WagerMode.POINTS, string> = {
  [WagerMode.MONEY]: 'Real Money',
  [WagerMode.POINTS]: 'Points',
};

const WagerCurrencySelector = () => {
  const {
    options,
    showSelector,
    selectedCategory,
    selectCategory,
    isLoading,
    autoMovedToPointsInsufficientUSD,
    dismissInsufficientUSDBanner,
    insufficientFundsNoFallback,
    dismissInsufficientFundsBanner,
  } = useWagerCurrency();

  // SMG-3455: the "out of funds" banner is server-driven (PLAYER_INSUFFICIENT_FUNDS)
  // and must surface even when the selector is hidden — a player with no points
  // fallback typically has a single available category, so showSelector is false.
  // Render it ahead of (and independently of) the single-availability gate.
  const outOfFundsBanner = insufficientFundsNoFallback ? (
    <Div
      testID="wager-insufficient-funds-banner"
      row
      alignItems="center"
      gap={8}
      py={10}
      px={12}
      rounded={8}
      bg="#2A2A1F"
      borderWidth={1}
      borderColor="#C9A227"
      mb={10}
    >
      <P flex={1} fontFamily="DavisSans-Medium" fs={13}>
        You&apos;re out of funds to keep playing. Add funds to continue.
      </P>
      <Btn
        testID="wager-insufficient-funds-dismiss"
        py={4}
        px={8}
        onPress={dismissInsufficientFundsBanner}
      >
        <P fontFamily="DavisSans-Medium" color="#C9A227">Dismiss</P>
      </Btn>
    </Div>
  ) : null;

  // Single-availability rule: hide the SELECTOR unless ≥2 categories are offered.
  // The out-of-funds banner is independent of the selector, so render it on its
  // own when the selector is hidden.
  if (!showSelector) {
    return outOfFundsBanner ? <Div mb={16}>{outOfFundsBanner}</Div> : null;
  }

  return (
    <Div mb={16}>
      {outOfFundsBanner}
      {autoMovedToPointsInsufficientUSD && (
        <Div
          testID="wager-currency-insufficient-usd-banner"
          row
          alignItems="center"
          gap={8}
          py={10}
          px={12}
          rounded={8}
          bg="#2A2A1F"
          borderWidth={1}
          borderColor="#C9A227"
          mb={10}
        >
          <P flex={1} fontFamily="DavisSans-Medium" fs={13}>
            Your USD balance is below the $5 minimum to play for real money — we&apos;ve switched
            you to Points.
          </P>
          <Btn
            testID="wager-currency-banner-dismiss"
            py={4}
            px={8}
            onPress={dismissInsufficientUSDBanner}
          >
            <P fontFamily="DavisSans-Medium" color="#C9A227">Dismiss</P>
          </Btn>
        </Div>
      )}
      <Div testID="wager-currency-selector" row gap={10}>
        {options.map((category) => {
        const selected = selectedCategory === category;
        return (
          <Btn
            key={category}
            testID={`wager-currency-option-${category === WagerMode.MONEY ? 'money' : 'points'}`}
            flex={1}
            py={10}
            px={16}
            rounded={8}
            borderWidth={2}
            borderColor={selected ? '#3369FF' : '#252526'}
            bg="#252526"
            opacity={isLoading ? 0.6 : 1}
            disabled={isLoading}
            justifyContent="center"
            onPress={() => selectCategory(category)}
          >
            <P fontFamily="DavisSans-Medium" ta="center">{CATEGORY_LABEL[category]}</P>
          </Btn>
        );
        })}
      </Div>
    </Div>
  );
};

export default WagerCurrencySelector;
